/* Kart Shed (rentalkartparts.com edition): physics core */
/* Kart Shed: physics, parts and timing core. Pure JS, no DOM, no three.js.
   Coordinates: x east, y north (metres). Yaw is counter-clockwise from +x. */
(function (root) {
  'use strict';
  const G = 9.81, RHO = 1.225, TAU = Math.PI * 2, TAMB = 18;
  const clamp = (v, a, b) => (v < a ? a : v > b ? b : v);
  const ssign = (v, eps) => v / (Math.abs(v) + eps); // smooth sign, avoids chatter at rest

  // ---------------------------------------------------------------- parts
  const ENGINES = {
    e200: { id: 'e200', name: '200cc', hp: 6.5, mass: 16, fuel: 0.0030 },
    e270: { id: 'e270', name: '270cc', hp: 9.0, mass: 25, fuel: 0.0040 },
    e390: { id: 'e390', name: '390cc', hp: 13.0, mass: 31, fuel: 0.0055 },
  };
  // peak torque chosen so the quoted hp lands at the governed 3600 rpm
  for (const k in ENGINES) ENGINES[k].tq = (ENGINES[k].hp * 745.7) / (0.93 * 3600 * TAU / 60);

  const TQ = [[0, .62], [1200, .74], [1800, .86], [2400, .97], [2800, 1], [3200, .985], [3600, .93],
    [4200, .84], [4800, .69], [5400, .50], [6200, .30]];
  function tqShape(rpm) {
    if (rpm <= TQ[0][0]) return TQ[0][1];
    for (let i = 1; i < TQ.length; i++) {
      if (rpm <= TQ[i][0]) {
        const a = TQ[i - 1], b = TQ[i];
        return a[1] + (b[1] - a[1]) * (rpm - a[0]) / (b[0] - a[0]);
      }
    }
    return TQ[TQ.length - 1][1];
  }

  const LIMIT = { governed: 3650, flywheel: 5400 };
  const CLUTCH_T = 17;            // clutch drum sprocket teeth (fixed)
  const REAR_MIN = 46, REAR_MAX = 86;
  const SPROCKETS = [46, 50, 54, 58, 62, 66, 70, 74, 78, 82, 86];

  const TYRES = {
    hard:   { id: 'hard',   name: 'Hard',   mu: 1.00, tOpt: 60, heat: 0.80, wear: 1.0, pOpt: 13.0, rear: 1.0 },
    medium: { id: 'medium', name: 'Medium', mu: 1.11, tOpt: 54, heat: 1.00, wear: 2.0, pOpt: 11.5, rear: 1.03 },
    soft:   { id: 'soft',   name: 'Soft',   mu: 1.22, tOpt: 48, heat: 1.25, wear: 4.0, pOpt: 10.5, rear: 1.13 },
  };
  // softer axle flexes more: more rear grip, but binds (scrubs speed) when grip is high
  const AXLES = {
    soft:   { id: 'soft',   name: 'Soft',   rear: 1.06, scrub: 0.060 },
    medium: { id: 'medium', name: 'Medium', rear: 1.00, scrub: 0.036 },
    hard:   { id: 'hard',   name: 'Hard',   rear: 0.94, scrub: 0.018 },
  };

  const K = {
    L: 1.05, a: 0.60, b: 0.45, h: 0.25, rw: 0.140, Iz: 44, CdA: 0.50, crr: 0.016,
    chassis: 72, driver: 78, steerLock: 0.42, steerRate: 3.2,
    aPeakF: 0.115, aPeakR: 0.095, shapeC: 1.22, axleYaw: 55,
    capRear: 0.85, capRearBoth: 0.62, capFront: 0.58, tank: 3.0,
    heat: 0.092, heatFront: 1.38, cool: 0.028, wear: 5.2e-7, padWear: 2.2e-7,
    pitSpeed: 4.5,
  };
  // A locked rear axle. axleYaw: how much of its yaw resistance is left. nudge: the push (N m per N of rear grip) that
  // starts it round; small, so a short straight stab stays straight and a held one, or any steering, brings it round.
  // scrub: how hard sliding tyres damp the rotation (N m per rad/s per N of rear grip), for `after` seconds past the lock.
  const LOCK = { axleYaw: 0.1, nudge: 0.008, scrub: 0.1, after: 2.5 };
  // how much grip each surface loses when it's raining
  const WET = { track: 0.32, kerb: 0.5, grass: 0.35, pit: 0.25 };
  const SHAPE_B = Math.tan(Math.PI / (2 * K.shapeC));
  function tyreShape(alpha, peak) { return Math.sin(K.shapeC * Math.atan(SHAPE_B * alpha / peak)); }

  /* Average the properties of the compounds fitted on one axle (mixing is allowed). */
  const profCache = {};
  function tyreProfile(list) {
    const ids = list.filter(Boolean);
    if (!ids.length) return TYRES.hard;
    const key = ids.slice().sort().join('+');
    if (profCache[key]) return profCache[key];
    if (ids.every((i) => i === ids[0])) return (profCache[key] = TYRES[ids[0]]);
    const o = { id: 'mixed', name: 'Mixed' };
    for (const f of ['mu', 'tOpt', 'heat', 'wear', 'pOpt', 'rear']) o[f] = ids.reduce((a, i) => a + TYRES[i][f], 0) / ids.length;
    return (profCache[key] = o);
  }

  /* Fill in a setup: a legacy one ({tyre, brakes}) or one built from fitted parts. Mutates and returns S.
     The 'done' flag is non-enumerable, so a copy made with Object.assign is normalised afresh. */
  function normSetup(S) {
    if (Object.prototype.hasOwnProperty.call(S, '_n')) return S;
    const d = { engine: 'e270', governor: true, rear: 62, chain: true, psi: 14, axle: 'medium', pads: true, frontCal: 0, calSide: 0 };
    for (const k in d) if (!(k in S)) S[k] = d[k];
    if (S.tyre) { S.tFL = S.tFR = S.tRL = S.tRR = S.tyre; }          // legacy: one compound on all four
    if (!('tFL' in S)) { S.tFL = S.tFR = S.tRL = S.tRR = 'hard'; }
    if (S.brakes) S.frontCal = S.brakes === 'both' ? 2 : 0;           // legacy
    S.wheelsF = (S.tFL ? 1 : 0) + (S.tFR ? 1 : 0);
    S.wheelsR = S.axle ? (S.tRL ? 1 : 0) + (S.tRR ? 1 : 0) : 0;
    S.tf = tyreProfile([S.tFL, S.tFR]);
    S.tr = tyreProfile([S.tRL, S.tRR]);
    S.compound = S.tf.id === S.tr.id ? S.tf.id : 'mixed';
    S.connected = !!(S.engine && S.chain && S.rear && S.axle);
    Object.defineProperty(S, '_n', { value: true, enumerable: false });
    return S;
  }
  function normCond(C) {
    if (C.tyres != null && C.tyresF == null) { C.tyresF = C.tyres; C.tyresR = C.tyres; }
    for (const k of ['tyresF', 'tyresR', 'lube', 'chain', 'pads', 'engine']) if (C[k] == null) C[k] = 1;
    if (C.fuel == null) C.fuel = K.tank;
    return C;
  }

  function defaultSetup() {
    return { engine: 'e270', governor: true, rear: 62, tyre: 'hard', psi: 14, axle: 'medium', brakes: 'rear' };
  }
  // the kart as you find it: used, dry chain, not much fuel
  function foundCondition() { return { fuel: 0.6, tyresF: 0.72, tyresR: 0.72, lube: 0.25, chain: 0.8, pads: 0.55, engine: 0.92 }; }
  function freshCondition() { return { fuel: K.tank, tyresF: 1, tyresR: 1, lube: 1, chain: 1, pads: 1, engine: 1 }; }

  const ratioOf = (S) => (S.rear || 62) / CLUTCH_T;
  const limitOf = (S) => (S.governor ? LIMIT.governed : LIMIT.flywheel);
  // speed at the rev limit in km/h
  function topSpeedAtLimit(S) { return (limitOf(S) * TAU / 60 / ratioOf(S)) * K.rw * 3.6; }
  function massOf(S, C) { return K.chassis + K.driver + (S.engine ? ENGINES[S.engine].mass : 0) + C.fuel * 0.74; }

  // ---------------------------------------------------------------- the paddock: pit road, shed, bays
  // Shed interior runs x 2..18, y -28..-18 with the roller door in the north wall at x 5..11.
  // The pit road joins the south edge of the main straight to that door.
  const PIT = {
    shed: { x0: 2, x1: 18, y0: -28, y1: -18, t: 0.2, doorX0: 5, doorX1: 11 },
    road: { x0: 5, x1: 11, y0: -18.4, y1: -3.6 },
    bays: [{ x: 8, y: -24.6 }, { x: 11.6, y: -24.6 }, { x: 4.6, y: -24.6 }, { x: 15.1, y: -24.6 }],
    // static obstacles inside the shed [x0, x1, y0, y1]
    boxes: [[2.4, 17.6, -27.9, -27.05], [16.95, 17.85, -26.9, -19.1], [2.15, 3.05, -26.9, -19.6], [2.3, 4.4, -19.9, -18.35]],
    bin: { x: 16.85, y: -18.75, r: 0.38 },
  };
  function inRect(x, y, r, m) { return x >= r.x0 - (m || 0) && x <= r.x1 + (m || 0) && y >= r.y0 - (m || 0) && y <= r.y1 + (m || 0); }
  function shedInner() { const s = PIT.shed; return { x0: s.x0 + s.t, x1: s.x1 - s.t, y0: s.y0 + s.t, y1: s.y1 - s.t }; }
  const SHED_IN = shedInner();
  /* 'shed', 'pit' (the road) or null */
  function paddockZone(x, y) {
    if (inRect(x, y, SHED_IN)) return 'shed';
    if (inRect(x, y, PIT.road)) return 'pit';
    return null;
  }

  // ---------------------------------------------------------------- track
  const TRACK_SEQ = [['S', 95], ['L', 180, 9], ['S', 18], ['R', 60, 14], ['L', 60, 14], ['S', 10], ['R', 90, 17],
    ['S', 10], ['R', 90, 11], ['S', 30], ['L', 45, 24], ['S', 6], ['L', 135, 10], ['S', 16], ['R', 25, 20],
    ['L', 50, 20], ['R', 25, 20], ['S', 30], ['L', 60, 16], ['S', 8], ['L', 30, 30], ['S', 50], ['L', 90, 13]];
  const TRACK_CLOSE = [17, 21];
  const CORNER_NAMES = ['', 'Hairpin', '', 'Esses', 'Esses', '', 'Sweeper', '', 'Tower', '', 'Kink', '', 'Top hairpin',
    '', 'Flick', 'Flick', 'Flick', '', 'Willows', '', 'Willows', '', 'Last corner'];

  // ---------------------------------------------------------------- the circuits
  // Every circuit starts at the same place: P[0] is the start of the pit straight, next to the pit road, with the shed to the
  // south. The home circuit is a turtle sequence (straights and arcs). The other two are centrelines traced from real
  // circuits and stored in decimetres, run through a Catmull-Rom spline. The lap checker (kart-shed-board) builds all three from this same table.
  const TRACKS = {
    home: { id: 'home', name: 'Shed Circuit', blurb: 'The test track out the back. Half a kilometre, 12 corners, and the one the monthly prize is raced on.',
      seq: TRACK_SEQ, close: TRACK_CLOSE, names: CORNER_NAMES, halfW: 4.0, runoff: 6.0,
      medals: [39.5, 37.5, 35.5, 33.5], marshals: [110, 226, 302, 420, 493], board: true },
    kesteven: { id: 'kesteven', name: 'Kesteven International', blurb: 'Just over a kilometre of the long one down the road: a flat-out pit straight, the double hairpin, the loop and the bowl. Fast.',
      halfW: 4.0, runoff: 3.5, medals: [78.5, 74.5, 70.5, 66.5], marshals: [190, 528, 708, 897, 948],
      secrets: {"whistle": [528, "Horseshoe"], "duck": [948, "Bowl"]},
      names: [[160, 265, 'Barn hairpin'], [365, 415, 'Hook'], [500, 555, 'Horseshoe'], [650, 730, 'Esses'], [810, 850, 'Chapel'], [855, 935, 'Long hairpin'], [935, 970, 'Bowl'], [985, 1035, 'Sweep'], [1035, 1060, 'Pit bend']],
      pts: [[200,0],[80,0],[-40,0],[-160,0],[-280,0],[-400,1],[-520,0],[-640,-1],[-760,-3],[-880,-4],[-1000,-4],[-1120,-3],[-1240,-2],[-1360,-1],[-1475,1],
      [-1510,5],[-1548,15],[-1586,28],[-1617,44],[-1646,63],[-1673,86],[-1697,111],[-1721,144],[-1744,182],[-1759,214],[-1766,238],[-1769,263],[-1768,298],
      [-1762,342],[-1750,396],[-1741,424],[-1727,451],[-1708,480],[-1685,507],[-1659,530],[-1626,553],[-1591,573],[-1563,584],[-1539,590],[-1504,594],
      [-1385,596],[-1265,598],[-1145,600],[-1025,600],[-905,601],[-785,601],[-665,601],[-545,601],[-425,602],[-345,601],[-315,598],[-291,591],[-273,583],
      [-257,570],[-244,555],[-234,538],[-228,519],[-227,499],[-229,479],[-236,460],[-246,443],[-260,428],[-276,416],[-294,408],[-318,401],[-353,399],
      [-473,398],[-593,398],[-713,398],[-833,398],[-953,398],[-1073,398],[-1193,398],[-1313,398],[-1373,397],[-1402,393],[-1426,387],[-1445,379],[-1461,367],
      [-1475,353],[-1486,336],[-1491,322],[-1493,307],[-1493,292],[-1488,273],[-1479,255],[-1467,239],[-1452,226],[-1434,216],[-1411,209],[-1381,204],
      [-1326,202],[-1206,202],[-1086,201],[-966,201],[-846,201],[-726,200],[-606,200],[-486,200],[-366,199],[-246,199],[-201,201],[-172,206],[-148,213],
      [-125,224],[-105,239],[-88,257],[-74,277],[-63,300],[-56,324],[-52,354],[-47,438],[-41,478],[-33,507],[-24,530],[-13,547],[0,562],[16,574],[39,585],
      [67,594],[102,600],[147,602],[267,603],[387,603],[507,603],[627,603],[747,603],[867,604],[987,604],[1097,603],[1126,600],[1150,592],[1168,584],
      [1184,572],[1198,557],[1209,541],[1218,523],[1224,498],[1227,464],[1228,344],[1228,224],[1225,184],[1218,144],[1209,111],[1198,88],[1184,67],[1164,45],
      [1140,27],[1114,12],[1091,3],[1067,-2],[1042,-3],[1012,0],[983,8],[960,17],[939,30],[920,47],[907,62],[898,80],[892,99],[889,119],[889,139],[893,158],
      [900,177],[914,198],[940,228],[967,258],[983,283],[996,310],[1002,334],[1005,354],[1003,374],[997,393],[987,410],[973,425],[961,434],[943,442],
      [924,447],[889,449],[769,447],[649,446],[584,444],[549,439],[515,430],[483,417],[452,401],[424,380],[399,356],[376,329],[360,303],[348,276],[338,242],
      [331,198],[324,108],[318,79],[310,55],[298,33],[289,21],[278,12],[265,5],[245,1]] },
    clay: { id: 'clay', name: 'Clay Lane', blurb: 'The tight one up by the coast: 750 metres folded into a field, hairpin after hairpin and nowhere to rest.',
      halfW: 3.3, runoff: 2.5, medals: [65.0, 61.5, 58.5, 55.0], marshals: [144, 262, 367, 568, 720],
      secrets: {"whistle": [144, "Clay hairpin"], "duck": [367, "Island"]},
      names: [[34, 86, 'Kink'], [125, 160, 'Clay hairpin'], [160, 195, 'Switchback'], [195, 245, 'Hedge corner'], [248, 275, 'Top corner'], [320, 400, 'Island'], [455, 490, 'Gate'], [492, 540, 'Esses'], [538, 598, 'Bottom hairpin'], [600, 640, 'Pinch'], [648, 698, 'Sweep'], [698, 747, 'Pit hairpin']],
      pts: [[200,0],[80,0],[-40,0],[-160,0],[-205,1],[-220,4],[-243,13],[-272,33],[-303,48],[-361,87],[-388,101],[-407,107],[-427,109],[-451,107],[-505,96],
      [-545,89],[-599,86],[-719,82],[-799,80],[-869,84],[-919,85],[-964,92],[-1018,101],[-1047,108],[-1082,111],[-1096,116],[-1108,125],[-1118,136],
      [-1124,150],[-1127,164],[-1127,179],[-1123,194],[-1116,207],[-1105,217],[-1092,224],[-1068,231],[-1003,240],[-968,241],[-933,240],[-909,243],
      [-890,249],[-876,256],[-865,265],[-855,277],[-850,291],[-848,306],[-850,321],[-856,335],[-864,347],[-878,361],[-911,384],[-925,390],[-939,393],
      [-964,393],[-1009,389],[-1034,391],[-1068,400],[-1091,409],[-1108,419],[-1122,434],[-1135,455],[-1147,482],[-1154,506],[-1156,526],[-1155,546],
      [-1138,619],[-1132,633],[-1123,645],[-1108,658],[-1087,671],[-1060,683],[-946,721],[-912,730],[-892,732],[-872,729],[-843,722],[-828,721],[-813,723],
      [-795,729],[-773,742],[-737,769],[-668,826],[-638,845],[-598,865],[-565,878],[-512,890],[-477,895],[-432,897],[-312,895],[-227,894],[-203,897],
      [-184,904],[-167,915],[-145,935],[-116,963],[-91,980],[-69,991],[-36,1002],[3,1011],[28,1014],[48,1012],[67,1005],[98,989],[118,975],[133,962],
      [148,942],[169,902],[179,874],[188,835],[191,815],[190,800],[186,785],[180,772],[171,760],[152,743],[115,718],[67,691],[44,681],[25,677],[-30,675],
      [-105,665],[-130,665],[-204,676],[-249,680],[-288,680],[-398,676],[-433,677],[-478,681],[-533,680],[-553,676],[-571,668],[-587,657],[-605,639],
      [-640,591],[-651,574],[-655,559],[-656,544],[-652,525],[-631,453],[-620,415],[-608,387],[-595,366],[-572,333],[-549,294],[-536,279],[-520,267],
      [-507,260],[-492,257],[-467,255],[-398,258],[-368,256],[-343,250],[-311,236],[-239,201],[-206,190],[-167,180],[-138,177],[-108,177],[-73,182],
      [-54,187],[-40,194],[-29,203],[-19,215],[-11,233],[-5,257],[-3,277],[-5,297],[-11,316],[-22,333],[-35,347],[-52,359],[-75,368],[-104,376],[-133,379],
      [-208,379],[-278,385],[-317,392],[-346,401],[-368,412],[-385,424],[-394,436],[-400,450],[-402,464],[-401,479],[-396,493],[-389,506],[-378,517],
      [-365,524],[-346,531],[-326,533],[-276,533],[-191,533],[-111,531],[-77,524],[-43,515],[-21,504],[8,485],[31,465],[91,398],[120,370],[144,353],
      [175,337],[212,322],[246,313],[275,305],[289,299],[305,287],[322,269],[337,249],[348,227],[358,198],[366,159],[368,129],[367,104],[362,85],[352,67],
      [340,51],[321,35],[292,16],[274,7],[255,2],[225,0]] },
  };
  const TRACK_IDS = ['home', 'kesteven', 'clay'];

  /* A closed centripetal Catmull-Rom spline through stored decimetre points, sampled every ~0.5 m, with curvature. */
  function spline(src) {
    const n = src.length, Q = src.map((p) => [p[0] / 10, p[1] / 10]);
    const lerp = (a, b, t) => [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];
    const out = [];
    for (let i = 0; i < n; i++) {
      const p0 = Q[(i - 1 + n) % n], p1 = Q[i], p2 = Q[(i + 1) % n], p3 = Q[(i + 2) % n];
      const t1 = Math.sqrt(Math.hypot(p1[0] - p0[0], p1[1] - p0[1])), t2 = t1 + Math.sqrt(Math.hypot(p2[0] - p1[0], p2[1] - p1[1]));
      const t3 = t2 + Math.sqrt(Math.hypot(p3[0] - p2[0], p3[1] - p2[1]));
      const m = Math.max(1, Math.ceil(Math.hypot(p2[0] - p1[0], p2[1] - p1[1]) / 0.5));
      for (let j = 0; j < m; j++) {
        const t = t1 + (t2 - t1) * j / m;
        const A1 = lerp(p0, p1, t / t1), A2 = lerp(p1, p2, (t - t1) / (t2 - t1)), A3 = lerp(p2, p3, (t - t2) / (t3 - t2));
        const B1 = lerp(A1, A2, t / t2), B2 = lerp(A2, A3, (t - t1) / (t3 - t1));
        const C = lerp(B1, B2, (t - t1) / (t2 - t1));
        out.push({ x: C[0], y: C[1], k: 0, seg: i });
      }
    }
    const N = out.length;
    for (let i = 0; i < N; i++) {
      const a = out[(i - 1 + N) % N], b = out[i], c = out[(i + 1) % N];
      const abx = b.x - a.x, aby = b.y - a.y, bcx = c.x - b.x, bcy = c.y - b.y;
      const la = Math.hypot(abx, aby), lb = Math.hypot(bcx, bcy), lc = Math.hypot(c.x - a.x, c.y - a.y);
      out[i].k = la * lb * lc > 0 ? (2 * (abx * bcy - aby * bcx)) / (la * lb * lc) : 0;
    }
    return out;
  }

  function turtle(seq) {
    let x = 0, y = 0, h = 0;
    const pts = [{ x, y, k: 0, seg: 0 }];
    seq.forEach((it, si) => {
      if (it[0] === 'S') {
        const L = it[1], n = Math.max(1, Math.ceil(L / 0.5));
        for (let i = 1; i <= n; i++) pts.push({ x: x + Math.cos(h) * L * i / n, y: y + Math.sin(h) * L * i / n, k: 0, seg: si });
        x += Math.cos(h) * L; y += Math.sin(h) * L;
      } else {
        const ang = it[1] * Math.PI / 180, R = it[2], sg = it[0] === 'L' ? 1 : -1;
        const cx = x + Math.cos(h + sg * Math.PI / 2) * R, cy = y + Math.sin(h + sg * Math.PI / 2) * R;
        const a0 = Math.atan2(y - cy, x - cx), n = Math.max(2, Math.ceil(ang * R / 0.5));
        for (let i = 1; i <= n; i++) {
          const a = a0 + sg * ang * i / n;
          pts.push({ x: cx + Math.cos(a) * R, y: cy + Math.sin(a) * R, k: sg / R, seg: si });
        }
        x = pts[pts.length - 1].x; y = pts[pts.length - 1].y; h += sg * ang;
      }
    });
    return { pts, x, y, h };
  }

  function solveClosure(seq, idx) {
    const s = seq.map((a) => a.slice());
    const endAt = (l1, l2) => { s[idx[0]][1] = l1; s[idx[1]][1] = l2; const t = turtle(s); return [t.x, t.y]; };
    const e0 = endAt(0, 0), ea = endAt(1, 0), eb = endAt(0, 1);
    const A = [[ea[0] - e0[0], eb[0] - e0[0]], [ea[1] - e0[1], eb[1] - e0[1]]];
    const det = A[0][0] * A[1][1] - A[0][1] * A[1][0];
    const l1 = (-e0[0] * A[1][1] + e0[1] * A[0][1]) / det;
    const l2 = (-A[0][0] * e0[1] + A[1][0] * e0[0]) / det;
    s[idx[0]][1] = l1; s[idx[1]][1] = l2;
    return s;
  }

  function buildTrack(def) {
    def = def || TRACKS.home;
    let raw;
    if (def.seq) {
      raw = turtle(solveClosure(def.seq, def.close)).pts;
      raw.pop(); // last point duplicates the first
    } else raw = spline(def.pts);
    const cum = [0];
    for (let i = 1; i <= raw.length; i++) {
      const a = raw[i - 1], b = raw[i % raw.length];
      cum.push(cum[i - 1] + Math.hypot(b.x - a.x, b.y - a.y));
    }
    const length = cum[raw.length];
    const N = Math.round(length);
    const ds = length / N;
    const P = [];
    let j = 0;
    for (let i = 0; i < N; i++) {
      const s = i * ds;
      while (cum[j + 1] < s) j++;
      const a = raw[j], b = raw[(j + 1) % raw.length];
      const t = (s - cum[j]) / (cum[j + 1] - cum[j] || 1);
      P.push({ x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t, k: t < 0.5 ? a.k : b.k, seg: a.seg, s });
    }
    for (let i = 0; i < N; i++) {
      const a = P[(i - 1 + N) % N], b = P[(i + 1) % N];
      const tx = b.x - a.x, ty = b.y - a.y, l = Math.hypot(tx, ty) || 1;
      P[i].tx = tx / l; P[i].ty = ty / l; P[i].nx = -ty / l; P[i].ny = tx / l;
    }
    const ks = P.map((p, i) => {
      let sum = 0;
      for (let o = -3; o <= 3; o++) sum += P[(i + o + N) % N].k;
      return sum / 7;
    });
    const halfW = def.halfW, runoff = def.runoff;
    const kerbL = new Uint8Array(N), kerbR = new Uint8Array(N);
    for (let i = 0; i < N; i++) {
      if (Math.abs(ks[i]) > 1 / 45) {
        for (let o = -4; o <= 4; o++) { const q = (i + o + N) % N; kerbL[q] = 1; kerbR[q] = 1; }
      }
    }
    const startIdx = Math.round(40 / ds);     // start/finish line 40 m down the main straight
    const spawnIdx = Math.round(10 / ds);     // on the main straight, next to the pit road
    const coarse = 4;
    function query(x, y) {
      let best = 1e18, bi = 0;
      for (let i = 0; i < N; i += coarse) {
        const dx = x - P[i].x, dy = y - P[i].y, d = dx * dx + dy * dy;
        if (d < best) { best = d; bi = i; }
      }
      for (let o = -coarse; o <= coarse; o++) {
        const i = (bi + o + N) % N;
        const dx = x - P[i].x, dy = y - P[i].y, d = dx * dx + dy * dy;
        if (d < best) { best = d; bi = i; }
      }
      const p = P[bi];
      const along = (x - p.x) * p.tx + (y - p.y) * p.ty;
      const d = (x - p.x) * p.nx + (y - p.y) * p.ny;
      return { i: bi, along, d, nx: p.nx, ny: p.ny, s: p.s + along };
    }
    return {
      P, N, ds, length, halfW, runoff, kerbL, kerbR, startIdx, spawnIdx, curv: ks, query,
      kerb: (i, left) => (left ? kerbL[i] : kerbR[i]) === 1,
      segName: def.seq ? (i) => def.names[P[i].seg] || '' : (i) => { const s = P[i].s; for (const r of def.names) if (s >= r[0] && s < r[1]) return r[2]; return ''; },
      id: def.id, def,
    };
  }

  /* Minimum-curvature line inside the track edges. Returns offsets (+ = left) and points. */
  function racingLine(track, margin = 1.15, iters = 8000) {
    const N = track.N, P = track.P, w = track.halfW - margin;
    const o = new Float64Array(N);
    for (let it = 0; it < iters; it++) {
      for (let i = 0; i < N; i++) {
        const a = (i - 1 + N) % N, b = (i + 1) % N;
        const mx = (P[a].x + P[a].nx * o[a] + P[b].x + P[b].nx * o[b]) / 2;
        const my = (P[a].y + P[a].ny * o[a] + P[b].y + P[b].ny * o[b]) / 2;
        const t = (mx - P[i].x) * P[i].nx + (my - P[i].y) * P[i].ny;
        o[i] = clamp(o[i] + (t - o[i]) * 0.6, -w, w);
      }
    }
    const x = new Float64Array(N), y = new Float64Array(N), kap = new Float64Array(N);
    for (let i = 0; i < N; i++) { x[i] = P[i].x + P[i].nx * o[i]; y[i] = P[i].y + P[i].ny * o[i]; }
    for (let i = 0; i < N; i++) {
      const a = (i - 3 + N) % N, b = (i + 3) % N;
      const ax = x[a], ay = y[a], bx = x[i], by = y[i], cx = x[b], cy = y[b];
      const cross = (bx - ax) * (cy - ay) - (by - ay) * (cx - ax);
      const la = Math.hypot(bx - ax, by - ay), lb = Math.hypot(cx - bx, cy - by), lc = Math.hypot(cx - ax, cy - ay);
      kap[i] = (2 * cross) / (la * lb * lc || 1);
    }
    return { x, y, kap, o };
  }

  // ---------------------------------------------------------------- kart
  function createKart(track, idx) {
    const i = idx == null ? track.spawnIdx : idx;
    const p = track.P[i];
    return createKartAt(track, p.x, p.y, Math.atan2(p.ty, p.tx));
  }
  function createKartAt(track, x, y, yaw) {
    const q = track.query(x, y);
    return {
      x, y, yaw, vx: 0, vy: 0, r: 0, steer: 0,
      rpm: 1600, cut: false, spinR: 0, spinF: 0, Tf: TAMB, Tr: TAMB, ax: 0, ay: 0,
      idx: q.i, lat: q.d, surface: 'track', zone: paddockZone(x, y), lockF: false, lockR: false, alphaF: 0, alphaR: 0,
      throttle: 0, brake: 0, limiter: false, slide: 0, dead: null, chainOff: false, sputter: 0,
      speed: 0, u: 0, events: [],
    };
  }

  function resetKart(k, track) {
    const q = track.query(k.x, k.y);
    const p = track.P[q.i];
    Object.assign(k, { x: p.x, y: p.y, yaw: Math.atan2(p.ty, p.tx), vx: 0, vy: 0, r: 0, steer: 0, idx: q.i, zone: null });
  }

  /* Keep a circle inside the drivable world: track + runoff, pit road, shed (with its walls and fittings).
     Returns the impact speed into whatever it hit, or 0. */
  function contain(k, track, radius) {
    let hit = 0;
    const push = (ox, oy, ex) => {
      k.x += ox * ex; k.y += oy * ex;
      const vn = -(k.vx * ox + k.vy * oy);
      if (vn > 0) {
        const hard = vn > 1.2;
        k.vx += (hard ? 1.3 : 1.0) * vn * ox; k.vy += (hard ? 1.3 : 1.0) * vn * oy;
        if (hard) { k.vx *= 0.8; k.vy *= 0.8; k.r *= 0.4; } else { k.vx *= 0.995; k.vy *= 0.995; }
        hit = Math.max(hit, vn);
      }
    };
    const z = paddockZone(k.x, k.y);
    if (z === 'shed') {
      const s = SHED_IN, rr = radius;
      const inDoor = k.x > PIT.shed.doorX0 + rr && k.x < PIT.shed.doorX1 - rr;
      if (k.x < s.x0 + rr) push(1, 0, s.x0 + rr - k.x);
      if (k.x > s.x1 - rr) push(-1, 0, k.x - (s.x1 - rr));
      if (k.y < s.y0 + rr) push(0, 1, s.y0 + rr - k.y);
      if (k.y > s.y1 - rr && !inDoor) push(0, -1, k.y - (s.y1 - rr));
      for (const b of PIT.boxes) {
        const cx = clamp(k.x, b[0], b[1]), cy = clamp(k.y, b[2], b[3]);
        const dx = k.x - cx, dy = k.y - cy, d = Math.hypot(dx, dy);
        if (d < rr) { if (d > 1e-6) push(dx / d, dy / d, rr - d); else push(0, 1, rr); }
      }
      return hit;
    }
    if (z === 'pit') {
      // the road has walls where it runs beyond the track's own run-off
      const q = track.query(k.x, k.y);
      if (Math.abs(q.d) > track.halfW + track.runoff - 0.2) {
        const r = PIT.road;
        if (k.x < r.x0 + radius) push(1, 0, r.x0 + radius - k.x);
        if (k.x > r.x1 - radius) push(-1, 0, k.x - (r.x1 - radius));
      }
      return hit;
    }
    const q = track.query(k.x, k.y);
    const lim = track.halfW + track.runoff;
    if (Math.abs(q.d) > lim) {
      // left the run-off: allowed only when that puts you on the pit road
      if (!inRect(k.x, k.y, PIT.road, 0.02)) {
        const sg = Math.sign(q.d);
        push(-q.nx * sg, -q.ny * sg, Math.abs(q.d) - lim);
      }
    }
    return hit;
  }

  /* One physics step. S = setup (see normSetup), C = condition (mutated: wear, fuel), inp = {throttle, brake, steer, assist, wet}
     steer: +1 = full left. wet: 0 = dry, 1 = raining. Events are pushed to k.events. */
  function step(k, S, C, inp, dt, track) {
    normSetup(S);
    const eng = S.engine ? ENGINES[S.engine] : null, tyF = S.tf, tyR = S.tr, axle = AXLES[S.axle] || AXLES.medium;
    const m = massOf(S, C), W = m * G;

    // surface under the kart
    const q = track.query(k.x, k.y);
    k.idx = q.i; k.lat = q.d;
    k.zone = paddockZone(k.x, k.y);
    const ad = Math.abs(q.d);
    let surf = 'track';
    if (k.zone) surf = 'pit';
    else if (ad > track.halfW + 0.35) surf = ad < track.halfW + 1.3 && track.kerb(q.i, q.d > 0) ? 'kerb' : 'grass';
    k.surface = surf;
    // rain: slick tyres on a wet track have about two thirds of their dry grip; painted kerbs and wet grass are worse still
    const wet = clamp(inp.wet || 0, 0, 1);
    k.wet = wet;
    const sMu = (surf === 'grass' ? 0.55 : surf === 'kerb' ? 0.96 : surf === 'pit' ? 0.95 : 1) * (1 - wet * WET[surf]);
    const sRoll = surf === 'grass' ? 7 : surf === 'kerb' ? 1.6 : 1;

    // grip, per axle
    const tfn = (T, ty) => (T < ty.tOpt ? 1 - 0.16 * Math.min(1, ((ty.tOpt - T) / 22) ** 2) : 1 - 0.13 * Math.min(1, ((T - ty.tOpt) / 26) ** 2));
    const pfF = Math.max(0.8, 1 - 0.006 * (S.psi - tyF.pOpt) ** 2), pfR = Math.max(0.8, 1 - 0.006 * (S.psi - tyR.pOpt) ** 2);
    const wfF = 0.87 + 0.13 * clamp(C.tyresF, 0, 1), wfR = 0.87 + 0.13 * clamp(C.tyresR, 0, 1);
    const looseAxle = S.axleLoose ? 0.85 : 1;
    const muF = tyF.mu * pfF * tfn(k.Tf, tyF) * wfF * sMu * 0.985 * (S.wheelsF / 2);
    const muR = tyR.mu * pfR * tfn(k.Tr, tyR) * wfR * sMu * axle.rear * tyR.rear * looseAxle * (S.wheelsR / 2);
    const crrP = K.crr * Math.pow(11.5 / S.psi, 0.4) * sRoll;

    // car-frame velocity
    const cy = Math.cos(k.yaw), sy = Math.sin(k.yaw);
    const u = k.vx * cy + k.vy * sy, v = -k.vx * sy + k.vy * cy, r = k.r;
    const speed = Math.hypot(u, v);

    // steering: speed-sensitive lock and a rate limit
    const um = Math.max(Math.abs(u), 1);
    // about 1.3x what the tyres can use at this speed, so a full stab doesn't throw it away
    const lim = Math.min(K.steerLock, 1.3 * (12.9 / (um * um) + 0.03) + 0.015);
    let target = clamp(inp.steer || 0, -1, 1) * lim;
    if (inp.assist && speed > 3) {
      // stability assist: catch the rear by steering into the slide, the way a driver would
      const over = Math.abs(k.alphaR) - 0.85 * K.aPeakR;
      if (over > 0) { target += Math.sign(k.alphaR) * Math.min(0.35, over * 1.4); k.catching = true; } else k.catching = false;
      target = clamp(target, -K.steerLock, K.steerLock);
    } else {
      k.catching = false;
      if (speed > 3) {
        // assists off: the front wheels still follow a slide, as a kart's do when the driver stops fighting the wheel
        // (the assist's rule above without its 0.35 rad cap), so letting go of the stick or of the throttle catches it,
        // and a stamped brake slides the kart instead of spinning it. The help is withdrawn only while the stick is held
        // INTO the slide with the throttle open: that is the driver asking for a power slide, and it leaves the wet,
        // throttle-balanced slide in their hands.
        const over = Math.abs(k.alphaR) - 0.85 * K.aPeakR;
        if (over > 0) {
          const sg = Math.sign(k.alphaR), into = Math.max(0, -clamp(inp.steer || 0, -1, 1) * sg);
          target = clamp(target + sg * (over * 1.4 * (1 - into * clamp(inp.throttle || 0, 0, 1))), -K.steerLock, K.steerLock);
        }
      }
    }
    k.steer += clamp(target - k.steer, -K.steerRate * dt, K.steerRate * dt);
    const d = k.steer, cd = Math.cos(d), sd = Math.sin(d);

    // axle loads with longitudinal weight transfer
    let NF = W * K.b / K.L - m * k.ax * K.h / K.L;
    let NR = W * K.a / K.L + m * k.ax * K.h / K.L;
    NF = Math.max(NF, 0.15 * W); NR = Math.max(NR, 0.2 * W);
    const DF = Math.max(1e-3, muF * NF), DR = Math.max(1e-3, muR * NR);

    // ---- drivetrain
    if (eng && !k.dead && C.engine <= 0) { k.dead = 'engine'; k.events.push({ type: 'engine' }); }
    let thr = clamp(inp.throttle || 0, 0, 1);
    if (!eng || k.dead || C.fuel <= 0) thr = 0;
    if (eng && C.fuel > 0 && C.fuel < 0.08) { // fuel starvation: it coughs
      k.sputter -= dt;
      if (k.sputter < -0.35) k.sputter = 0.12 + Math.random() * 0.2;
      if (k.sputter > 0) thr = 0;
    }
    const drive = S.connected && !k.chainOff;
    const ratio = ratioOf(S), limit = limitOf(S);
    const rpmWheel = drive ? Math.max(0, u / K.rw * 60 / TAU * ratio) : 0;
    const idle = eng && !k.dead && C.fuel > 0 ? 1600 : 0, lockRpm = 2900;
    let rpmT;
    if (!drive) rpmT = idle ? idle + thr * (Math.min(limit, 5000) - idle) : 0;
    else if (rpmWheel >= lockRpm) rpmT = rpmWheel;
    else rpmT = Math.max(rpmWheel, idle + thr * (2950 - idle));
    k.rpm += (rpmT - k.rpm) * Math.min(1, dt * (drive && rpmWheel >= lockRpm ? 40 : 7));
    const rpm = k.rpm;
    let thrEff = thr;
    if (S.governor) {
      thrEff *= clamp((limit - rpm) / 170, 0, 1);            // governor closes the throttle smoothly
      k.limiter = thr > 0.8 && rpm > limit - 90;
    } else {
      if (rpm > limit) k.cut = true; else if (rpm < limit - 90) k.cut = false;
      if (k.cut) thrEff = 0;                                   // flywheel limiter: hard cut, it stutters
      k.limiter = thr > 0.8 && rpm > limit - 140;
    }
    k.pitLimiter = !!k.zone;
    if (k.zone) thrEff *= clamp((K.pitSpeed - speed) / 1.5, 0, 1);  // pit lane speed limiter
    const health = C.engine < 0.3 ? 0.78 + 0.73 * C.engine : 1;
    const engage = clamp((rpm - 2000) / 500, 0, 1);
    let Te = eng ? eng.tq * tqShape(rpm) * thrEff * health * engage : 0;
    if (eng && thr < 0.08 && rpmWheel >= lockRpm) Te -= 0.8 + rpm / 3000 * 0.9;  // engine braking
    const eta = 0.90 + 0.06 * clamp(C.lube, 0, 1);
    let Fdrive = drive ? Te * ratio * eta / K.rw : 0;
    k.tc = false;
    if (inp.assist && Fdrive > 0) {
      // traction assist: don't ask the rear tyres for more drive than they can give while cornering
      const u2 = Math.abs(k.alphaR) / K.aPeakR;
      const room = u2 >= 1 ? 0.25 : Math.max(0.25, Math.sqrt(1 - u2 * u2));
      const maxDrive = 0.88 * DR * room;
      if (Fdrive > maxDrive) { Fdrive = maxDrive; k.tc = true; }
    }

    // ---- brakes: rear caliper needs its pads; front calipers are optional, one or two
    const fvx = u, fvy = v + K.a * r;
    const uf = fvx * cd + fvy * sd, wfv = -fvx * sd + fvy * cd;
    const alphaF = Math.atan2(wfv, Math.max(Math.abs(uf), 2));
    let brakeIn = clamp(inp.brake || 0, 0, 1);
    const pad = 0.2 + 0.8 * clamp(C.pads, 0, 1);
    const capR = S.pads ? (S.frontCal > 0 ? K.capRearBoth : K.capRear) * W * pad : 0;
    const capF = S.frontCal > 0 ? K.capFront * W * pad * (S.frontCal / 2) : 0;
    if (inp.assist && brakeIn > 0 && (capR > 0 || capF > 0)) {
      // brake assist: hold each braked axle just under lock, backing off while that axle is busy cornering
      const room = (alpha, peak) => { const u2 = Math.abs(alpha) / peak; return u2 >= 1 ? 0 : Math.sqrt(1 - u2 * u2); };
      let lm = 1;
      if (capR > 0) lm = Math.min(lm, (0.92 * DR * room(k.alphaR, K.aPeakR)) / capR);
      if (capF > 0) lm = Math.min(lm, (0.92 * DF * room(k.alphaF, K.aPeakF)) / capF);
      brakeIn = Math.min(brakeIn, lm);
      k.assisting = brakeIn < (inp.brake || 0) - 0.02;
    } else k.assisting = false;
    const bF = brakeIn * capF;
    const halfStop = (vel) => m * Math.abs(vel) / dt * 0.45;  // braking never reverses the kart
    let FxF, FyF, lockF = false;
    if (bF > DF * 0.97 && Math.abs(uf) > 0.8) {
      const vs = Math.hypot(uf, wfv) || 1e-6;
      FxF = -0.85 * DF * uf / vs; FyF = -0.85 * DF * wfv / vs; lockF = true;
    } else {
      FxF = -Math.sign(uf) * Math.min(bF, halfStop(uf));
      const fyMax = DF * Math.sqrt(Math.max(0, 1 - (FxF / DF) ** 2));
      FyF = -fyMax * tyreShape(alphaF, K.aPeakF);
    }
    FxF -= crrP * NF * ssign(uf, 0.3) * (S.wheelsF / 2);

    // ---- rear axle (solid axle, drive + brake)
    const rvx = u, rvy = v - K.b * r;
    const alphaR = Math.atan2(rvy, Math.max(Math.abs(rvx), 2));
    const bR = brakeIn * capR;
    const scrub = axle.scrub * W * Math.pow(Math.min(Math.abs(k.ay) / G, 2), 3) * ssign(u, 0.5);
    let FxR, FyR, lockR = false, spin = false;
    if (bR > DR * 0.97 && Math.hypot(rvx, rvy) > 0.8) {
      // locked: both rear tyres just slide, pushing back against whichever way the axle is travelling.
      // They've nothing left to hold the back in line, so the fronts steer it round: stamp and hold, and it spins.
      const vs = Math.hypot(rvx, rvy) || 1e-6;
      FxR = -0.85 * DR * rvx / vs; FyR = -0.85 * DR * rvy / vs; lockR = true;
    } else {
      let fx = Fdrive - Math.sign(u) * Math.min(bR, halfStop(u)) - scrub;
      const fxMax = DR * 0.98;
      if (Math.abs(fx) > fxMax) { spin = fx > 0; fx = Math.sign(fx) * fxMax; }
      const fyMax = DR * Math.sqrt(Math.max(0, 1 - (fx / DR) ** 2));
      FyR = -fyMax * tyreShape(alphaR, K.aPeakR);
      FxR = fx;
    }
    FxR -= crrP * NR * ssign(u, 0.3) * (S.wheelsR / 2);

    // ---- sum and integrate (world-frame velocity)
    const aero = 0.5 * RHO * K.CdA * u * Math.abs(u);
    const FxFc = FxF * cd - FyF * sd, FyFc = FxF * sd + FyF * cd;
    let Fx = FxFc + FxR - aero, Fy = FyFc + FyR;
    if (surf === 'grass') { Fx -= 0.03 * W * ssign(u, 0.5); Fy -= 0.03 * W * ssign(v, 0.5); }
    // a missing wheel means a hub grinding along the ground
    const missing = (2 - S.wheelsF) * NF / 2 + (2 - S.wheelsR) * NR / 2;
    if (missing > 0) { Fx -= 0.75 * missing * ssign(u, 0.2); Fy -= 0.75 * missing * ssign(v, 0.2); }
    // solid rear axle resists yaw until the inside rear wheel lifts in a proper corner
    const lift = clamp(1 - Math.abs(k.ay) / (0.55 * G), 0.12, 1);
    // (a locked axle has both tyres sliding already, so it resists almost nothing)
    let Mz = K.a * FyFc - K.b * FyR - K.axleYaw * NR / W * r * lift * Math.min(1, speed / 3) * (lockR ? LOCK.axleYaw : 1);
    if (lockR && speed > 2) {
      // nothing is ever perfectly straight: a locked rear always starts to come round, the way it's already leaning
      const way = Math.abs(d) > 0.01 ? Math.sign(d) : Math.abs(r) > 0.02 ? Math.sign(r) : Math.abs(v) > 0.02 ? -Math.sign(v) : (k.idx & 1 ? 1 : -1);
      Mz += way * LOCK.nudge * DR;
    }
    // tyres sliding sideways scrub the rotation off, which is what lets a spin be caught.
    // Only during a lock-up and the couple of seconds after it, so ordinary cornering is exactly as it was.
    k.spun = lockR ? LOCK.after : Math.max(0, (k.spun || 0) - dt);
    if (k.spun > 0 && (lockR || Math.abs(alphaR) > 3 * K.aPeakR)) Mz -= LOCK.scrub * DR * r;
    if (S.frontCal === 1 && bF > 0) Mz += (S.calSide || 1) * Math.min(bF, DF) * 0.53;  // one front caliper pulls to its side
    const axc = Fx / m, ayc = Fy / m;
    k.vx += (axc * cy - ayc * sy) * dt;
    k.vy += (axc * sy + ayc * cy) * dt;
    k.r += Mz / K.Iz * dt;
    if (speed < 1.5) k.r *= 1 - Math.min(1, dt * 5 * (1.5 - speed));
    k.yaw += k.r * dt;
    k.x += k.vx * dt; k.y += k.vy * dt;
    k.ax += (axc - k.ax) * Math.min(1, dt * 10);
    k.ay += (ayc - k.ay) * Math.min(1, dt * 10);

    // come to rest cleanly
    const sp2 = Math.hypot(k.vx, k.vy);
    if ((brakeIn > 0.05 || thr < 0.02 || missing > 0) && thr < 0.05 && sp2 < 0.25) { k.vx = 0; k.vy = 0; k.r *= 0.5; }

    // ---- barriers, walls, pit road
    const hit = contain(k, track, 0.85);
    if (hit > 1.5) k.events.push({ type: 'hit', v: hit });

    // ---- tyres: temperature and wear
    const slipF = Math.abs(FyF * wfv) + (lockF ? 0.85 * DF * Math.hypot(uf, wfv) : Math.abs(FxF) * 0.03 * Math.abs(uf));
    const slipR = Math.abs(FyR * rvy) + (lockR ? 0.85 * DR * Math.hypot(rvx, rvy) : Math.abs(FxR) * 0.03 * Math.abs(u));
    const pm = Math.pow(S.psi / 11.5, 1.3);
    const cool = K.cool * (1 + speed / 12) * (1 + 1.2 * wet);   // standing water takes the heat out of them
    const hot = (T) => 1 + Math.max(0, T - 65) / 25;   // sheds heat faster once it's cooking
    k.Tf += (K.heat * K.heatFront * tyF.heat * pm * (0.4 * Math.min(slipF, 2000) + 0.012 * NF * speed) / 10 - cool * hot(k.Tf) * (k.Tf - TAMB)) * dt;
    k.Tr += (K.heat * tyR.heat * pm * (0.4 * Math.min(slipR, 2000) + 0.012 * NR * speed) / 10 - cool * hot(k.Tr) * (k.Tr - TAMB)) * dt;
    C.tyresF = Math.max(0, C.tyresF - K.wear * tyF.wear * 2 * slipF * dt);
    C.tyresR = Math.max(0, C.tyresR - K.wear * tyR.wear * 2 * slipR * dt);
    C.pads = Math.max(0, C.pads - K.padWear * (Math.min(bR, DR) + Math.min(bF, DF)) * Math.abs(u) * dt);

    // ---- chain, fuel, engine
    const dist = Math.abs(u) * dt;
    if (drive) {
      C.lube = Math.max(0, C.lube - 1.1e-4 * dist);
      C.chain = Math.max(0, C.chain - (8e-6 + 2.6e-5 * (1 - C.lube)) * dist);
      if (C.chain <= 0) { k.chainOff = true; k.events.push({ type: 'chain' }); }
    }
    if (eng && !k.dead && C.fuel > 0) {
      C.fuel = Math.max(0, C.fuel - eng.fuel * (0.12 + 0.88 * thrEff) * (rpm / 3600) * dt);
      if (C.fuel <= 0) k.events.push({ type: 'fuel' });
    }
    if (eng && !S.governor && !k.dead) {
      const over = Math.max(0, rpm - 4100) / 1300;
      C.engine = Math.max(0, C.engine - over * over * dt / 35);
      if (C.engine <= 0) { k.dead = 'engine'; k.events.push({ type: 'engine' }); }
    }

    // ---- bookkeeping for HUD, audio and pit notes
    k.alphaF = alphaF; k.alphaR = alphaR;
    k.throttle = thr; k.brake = brakeIn;
    if (lockR && !k.lockR) k.events.push({ type: 'lock', axle: 'rear' });
    if (lockF && !k.lockF) k.events.push({ type: 'lock', axle: 'front' });
    k.lockF = lockF; k.lockR = lockR; k.wheelspin = spin;
    k.slide = Math.max(Math.abs(alphaR) / K.aPeakR, Math.abs(alphaF) / K.aPeakF * 0.8, lockR || lockF ? 1.4 : 0);
    k.spinR += (lockR ? 0 : u / K.rw) * dt;
    k.spinF += (lockF ? 0 : uf / 0.125) * dt;
    k.speed = Math.hypot(k.vx, k.vy);
    k.u = u;
    k.grip = { DF, DR, W, pad, muF, muR };
  }

  // ---------------------------------------------------------------- timing session
  /* ref = optional all-time best {time, trace, ghost} used for the live delta and the ghost kart */
  function createSession(track, ref) {
    return {
      t: 0, lapT: 0, started: false, prevRel: null, lapNo: 0,
      cur: newLap(track), laps: [], best: null,
      refTime: ref ? ref.time : Infinity, bestTrace: ref ? ref.trace : null, bestGhost: ref ? ref.ghost : null,
      ghostClock: 0, delta: null, stats: newStats(),
    };
  }
  function newLap(track) {
    return { valid: true, reason: '', sector: 0, trace: new Float32Array(track.N).fill(-1), ghost: [], stats: newStats() };
  }
  function newStats() {
    return { limiterT: 0, peakRpm: 0, vmax: 0, maxTf: 0, maxTr: 0, sumTf: 0, sumTr: 0, t: 0,
      pushT: 0, looseT: 0, cornerT: 0, locks: 0, offs: 0, hits: 0, grassT: 0 };
  }

  function invalidate(ses, reason) {
    if (ses.cur.valid) { ses.cur.valid = false; ses.cur.reason = reason; }
  }

  /* Returns events when a lap completes: {type:'lap', lap} */
  function sessionUpdate(ses, k, S, dt, track) {
    const N = track.N;
    const rel = (k.idx - track.startIdx + N) % N;
    const out = [];
    ses.t += dt;
    if (ses.started) ses.lapT += dt;
    const st = ses.cur.stats;
    st.t += dt; st.sumTf += k.Tf * dt; st.sumTr += k.Tr * dt;
    st.maxTf = Math.max(st.maxTf, k.Tf); st.maxTr = Math.max(st.maxTr, k.Tr);
    st.peakRpm = Math.max(st.peakRpm, k.rpm); st.vmax = Math.max(st.vmax, k.speed || 0);
    if (k.limiter) st.limiterT += dt;
    if (Math.abs(k.ay) > 4 && k.speed > 6) {
      st.cornerT += dt;
      const f = Math.abs(k.alphaF) / K.aPeakF, r = Math.abs(k.alphaR) / K.aPeakR;
      if (f > 1.05 && f > r * 1.15) st.pushT += dt;
      if (r > 1.05 && r > f * 1.05) st.looseT += dt;
    }
    if (k.surface === 'grass') st.grassT += dt;
    for (const e of k.events) {
      if (e.type === 'lock') st.locks++;
      if (e.type === 'hit') st.hits++;
    }
    if (k.zone) { if (ses.started) invalidate(ses, 'Pit lane'); }
    else if (Math.abs(k.lat) > track.halfW + 3.0) {
      if (!ses.cur.offNow) { st.offs++; ses.cur.offNow = true; }
      if (ses.started) invalidate(ses, 'Off track');
    } else if (Math.abs(k.lat) < track.halfW) ses.cur.offNow = false;

    if (ses.prevRel != null) {
      let dRel = rel - ses.prevRel;
      if (dRel < -N / 2) dRel += N; if (dRel > N / 2) dRel -= N;
      const wrapped = ses.prevRel > N - 80 && rel < 80 && !k.zone;
      if (!wrapped && Math.abs(dRel) > 30 && ses.started) invalidate(ses, 'Cut the track');
      if (dRel < -3 && ses.started) invalidate(ses, 'Wrong way');
      if (ses.started) {
        if (ses.cur.sector === 0 && rel > N / 3 && rel < N / 2) ses.cur.sector = 1;
        if (ses.cur.sector === 1 && rel > (2 * N) / 3) ses.cur.sector = 2;
        if (ses.cur.trace[rel] < 0) ses.cur.trace[rel] = ses.lapT;
        if (ses.bestTrace && ses.bestTrace[rel] >= 0) ses.delta = ses.lapT - ses.bestTrace[rel];
      }
      ses.ghostClock += dt;
      if (ses.started && ses.ghostClock >= 0.05) {
        ses.ghostClock = 0;
        ses.cur.ghost.push(k.x, k.y, k.yaw, ses.lapT);
      }
      if (wrapped) {
        const p0 = ses.prevRel - N, p1 = rel;
        const frac = clamp((0 - p0) / (p1 - p0 || 1), 0, 1);
        const crossT = ses.lapT - dt * (1 - frac);
        if (ses.started) {
          const lap = finishLap(ses, crossT, S);
          out.push({ type: 'lap', lap });
        }
        ses.started = true;
        ses.lapNo += 1;
        ses.lapT = dt * (1 - frac);
        ses.cur = newLap(track);
        ses.cur.trace[rel] = ses.lapT;
        ses.delta = null;
      }
    }
    ses.prevRel = rel;
    k.events.length = 0;
    return out;
  }

  function finishLap(ses, time, S) {
    const c = ses.cur;
    const complete = c.sector === 2;
    const valid = c.valid && complete;
    // rec = this lap's own recording (same shape as a ghost), kept for every lap so a lap that isn't a best can still be checked
    const lap = { no: ses.lapNo, time, valid, reason: complete ? c.reason : (c.reason || 'Incomplete'), stats: c.stats, setup: summary(S), rec: c.ghost };
    ses.laps.push(lap);
    if (valid && (!ses.best || time < ses.best.time)) { ses.best = lap; lap.sessionBest = true; }
    if (valid && time < ses.refTime) {
      ses.refTime = time; ses.bestTrace = c.trace; ses.bestGhost = c.ghost;
      lap.pb = true; lap.trace = c.trace; lap.ghost = c.ghost;
    }
    return lap;
  }
  /* A plain copy of what matters in a setup, for run sheets */
  function summary(S) {
    normSetup(S);
    return { engine: S.engine, governor: S.governor, rear: S.rear, tFL: S.tFL, tFR: S.tFR, tRL: S.tRL, tRR: S.tRR,
      psi: S.psi, axle: S.axle, frontCal: S.frontCal, pads: S.pads, chain: S.chain };
  }

  // ---------------------------------------------------------------- pit notes
  function pitNotes(ses, S, C) {
    normSetup(S);
    const laps = ses.laps;
    const notes = [];
    if (!laps.length) return notes;
    const use = laps.filter((l) => l.valid).length ? laps.filter((l) => l.valid) : laps;
    const avg = (f) => use.reduce((a, l) => a + f(l.stats), 0) / use.length;
    const lim = avg((s) => s.limiterT);
    const peak = Math.max(...use.map((l) => l.stats.peakRpm));
    if (S.governor) {
      if (lim > 6) notes.push({ tag: 'Gearing', text: `On the governor for ${lim.toFixed(1)}s a lap. Try a smaller rear sprocket for more top speed.` });
      else if (lim < 1.5) notes.push({ tag: 'Gearing', text: `Only ${lim.toFixed(1)}s a lap on the governor, so it's geared long. Try a bigger rear sprocket for more drive out of the corners.` });
    } else {
      if (lim > 2.5) notes.push({ tag: 'Gearing', text: `Bouncing off the limiter for ${lim.toFixed(1)}s a lap, which is slow and eats the engine. Try a smaller rear sprocket.` });
      else if (peak < 4700) notes.push({ tag: 'Gearing', text: `Peak ${Math.round(peak)} rpm with no governor to stop it. A bigger rear sprocket uses those revs for drive.` });
    }
    const hotR = Math.max(...use.map((l) => l.stats.maxTr)), hotF = Math.max(...use.map((l) => l.stats.maxTf));
    const later = use.length > 1 ? use.slice(1) : use;
    const warmF = later.reduce((a, l) => a + l.stats.sumTf / Math.max(l.stats.t, 1), 0) / later.length;
    const warmR = later.reduce((a, l) => a + l.stats.sumTr / Math.max(l.stats.t, 1), 0) / later.length;
    const cook = hotR - S.tr.tOpt > hotF - S.tf.tOpt ? ['Rears', hotR, S.tr] : ['Fronts', hotF, S.tf];
    const cold = warmR - S.tr.tOpt < warmF - S.tf.tOpt ? ['Rears', warmR, S.tr] : ['Fronts', warmF, S.tf];
    if (cook[1] > cook[2].tOpt + 20) notes.push({ tag: 'Tyres', text: `${cook[0]} hit ${Math.round(cook[1])}\u00b0C, past their ~${Math.round(cook[2].tOpt)}\u00b0C window. Drop a psi or two.` });
    else if (cold[1] < cold[2].tOpt - 14) notes.push({ tag: 'Tyres', text: `${cold[0]} only averaged ${Math.round(cold[1])}\u00b0C and want ~${Math.round(cold[2].tOpt)}\u00b0C. More pressure warms them quicker.` });
    const corner = avg((s) => s.cornerT) || 1;
    const push = avg((s) => s.pushT) / corner, loose = avg((s) => s.looseT) / corner;
    if (push > 0.18 && push > loose) notes.push({ tag: 'Balance', text: `Front washing wide in ${Math.round(push * 100)}% of the cornering. A stiffer axle frees the rear, or softer front tyres bite harder.` });
    else if (loose > 0.14) notes.push({ tag: 'Balance', text: `Rear stepping out in ${Math.round(loose * 100)}% of the cornering. A softer axle or softer rear tyres give more rear grip.` });
    const locks = laps.reduce((a, l) => a + l.stats.locks, 0);
    if (locks >= 3) notes.push({ tag: 'Brakes', text: `Locked up ${locks} times. ${S.frontCal ? 'Squeeze the pedal instead of stamping on it.' : 'Rear-only brakes punish a stab. Squeeze it, or fit front calipers.'}` });
    if (C.lube < 0.3) notes.push({ tag: 'Chain', text: `Chain's dry. It's costing drive and wearing itself out. Lube it.` });
    const tw = Math.min(C.tyresF, C.tyresR);
    if (tw < 0.45) notes.push({ tag: 'Tyres', text: `${C.tyresF < C.tyresR ? 'Fronts' : 'Rears'} down to ${Math.round(tw * 100)}%. They've gone off: fresh wheels for a quick lap.` });
    if (C.pads < 0.3) notes.push({ tag: 'Brakes', text: `Pads nearly gone (${Math.round(C.pads * 100)}%).` });
    if (!S.governor && C.engine < 0.5) notes.push({ tag: 'Engine', text: `Engine at ${Math.round(C.engine * 100)}%. Without the governor it's living on borrowed time.` });
    if (C.fuel < 0.5) notes.push({ tag: 'Fuel', text: `${C.fuel.toFixed(2)} L left in the tank.` });
    return notes.slice(0, 5);
  }

  const api = {
    G, TAMB, ENGINES, TYRES, AXLES, LIMIT, CLUTCH_T, REAR_MIN, REAR_MAX, SPROCKETS, K, WET, LOCK, PIT, SHED_IN, TRACKS, TRACK_IDS, tqShape, tyreShape,
    defaultSetup, foundCondition, freshCondition, normSetup, normCond, tyreProfile, summary,
    ratioOf, limitOf, topSpeedAtLimit, massOf, paddockZone, inRect, contain,
    buildTrack, racingLine, createKart, createKartAt, resetKart, step, createSession, sessionUpdate, invalidate, pitNotes, clamp,
  };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  else root.KS = api;
})(typeof window !== 'undefined' ? window : globalThis);
